import { useState } from "react";
import type { AuditQuestion, AuditResult, PositionView } from "../../api/position";
import type { CheckItem, Platform } from "../../api/types";
import { AIFeedbackCard } from "../../components/AIFeedbackCard";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { Checks } from "../../components/Checks";
import { inputShell } from "../../components/Field";
import { GateFooter } from "../../components/GateFooter";
import { Guide } from "../../components/Guide";
import { StepChecklist } from "../../components/StepChecklist";
import { Pill } from "../../components/Pill";
import { ScreenHeader } from "../../components/ScreenHeader";
import { Section } from "../../components/Section";
import { safeHref } from "../../lib/format";

interface PositionScreenProps {
  view: PositionView;
  platforms: Platform[];
  auditQuestions: AuditQuestion[];
  aiBusy: boolean;
  busy: boolean;
  verifyResults: Record<string, { checks: CheckItem[]; keptPrevious: boolean } | undefined>;
  onNicheChange: (niche: string) => void;
  onReviewNiche: () => void;
  onSubmitAudit: (answers: { key: string; answer: string }[]) => void;
  onVisit: (platformId: string) => void;
  onVerify: (platformId: string, url: string) => void;
  onComplete: () => void;
}

function Audit({
  questions,
  result,
  busy,
  onSubmit,
}: {
  questions: AuditQuestion[];
  result: AuditResult;
  busy: boolean;
  onSubmit: PositionScreenProps["onSubmitAudit"];
}) {
  const [answers, setAnswers] = useState<Record<string, string>>(() => {
    try {
      return result.answers ? (JSON.parse(result.answers) as Record<string, string>) : {};
    } catch {
      return {};
    }
  });
  const [editing, setEditing] = useState(result.score == null);
  /*
   * Leave the form as soon as the server returns a score. `editing` was only seeded on mount, so a first-time
   * participant (score null at load) stayed on the form after a successful submit and never saw the result.
   * Keyed on takenAt so a retake shows the new result too.
   */
  const [shownTakenAt, setShownTakenAt] = useState(result.takenAt);
  if (result.takenAt !== shownTakenAt) {
    setShownTakenAt(result.takenAt);
    if (result.score != null) setEditing(false);
  }
  const complete = questions.every((q) =>
    q.free
      ? (answers[q.key] ?? "").trim().split(/\s+/).filter(Boolean).length >= 5
      : Boolean(answers[q.key]),
  );

  if (!editing && result.score != null) {
    return (
      <Section
        title="Your Gig-Work Readiness Audit"
        hint="Where you started."
        status={<Pill ok okText={`${result.score}%`} />}
      >
        <p className="m-0 mb-1 text-[14px]">
          Your starting score is <b className="text-accent tabular">{result.score}%</b>. It reflects
          how ready you are today, not how good you are. Each step you finish moves it up.
        </p>
        {result.feedback ? (
          <AIFeedbackCard feedback={result.feedback} title="Coach's note" />
        ) : null}
        <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
          Retake the audit
        </Button>
      </Section>
    );
  }

  return (
    <Section
      title="Start here: your Gig-Work Readiness Audit"
      hint="Five quick questions give you a starting score to beat."
    >
      <Guide guideKey="audit" done={false} />
      {questions.map((q) => (
        <fieldset key={q.key} className="mb-4 border-0 p-0">
          <legend className="mb-2 text-[14px] font-semibold">{q.q}</legend>
          {q.free ? (
            <textarea
              aria-label={q.q}
              rows={2}
              className={inputShell}
              value={answers[q.key] ?? ""}
              onChange={(event) => setAnswers({ ...answers, [q.key]: event.target.value })}
            />
          ) : (
            <div className="flex flex-col gap-1.5">
              {q.options.map((option) => (
                <label key={option.v} className="flex items-center gap-2 text-[14px]">
                  <input
                    type="radio"
                    name={q.key}
                    checked={answers[q.key] === option.v}
                    onChange={() => setAnswers({ ...answers, [q.key]: option.v })}
                  />
                  {option.t}
                </label>
              ))}
            </div>
          )}
        </fieldset>
      ))}
      <Button
        disabled={!complete || busy}
        onClick={() =>
          onSubmit(questions.map((q) => ({ key: q.key, answer: answers[q.key] ?? "" })))
        }
      >
        {busy ? "Scoring..." : "See my score"}
      </Button>
    </Section>
  );
}

function PlatformCard({
  platform,
  profile,
  result,
  onVisit,
  onVerify,
}: {
  platform: Platform;
  profile: PositionView["profiles"][number] | undefined;
  result: { checks: CheckItem[]; keptPrevious: boolean } | undefined;
  onVisit: () => void;
  onVerify: (url: string) => void;
}) {
  const [url, setUrl] = useState(profile?.profileUrl ?? "");
  const verified = Boolean(profile?.verified);
  return (
    <div
      className={`rounded-[var(--radius-control)] border-[1.5px] p-3.5 ${verified ? "border-positive" : "border-border"}`}
    >
      <div className="flex items-center gap-3">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-[9px] bg-accent font-bold text-on-accent">
          {platform.letter}
        </span>
        <div className="flex-1">
          <div className="font-bold">{platform.name}</div>
          <div className="text-[12.5px] text-text-muted">{platform.description}</div>
        </div>
        {verified ? <span className="text-[12px] font-bold text-positive">✓ Verified</span> : null}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <a
          href={safeHref(platform.referralUrl)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onVisit}
          className="inline-flex min-h-9 items-center rounded-[var(--radius-chip)] border border-border px-3 text-[13px] font-semibold text-accent"
        >
          Open {platform.name} ↗
        </a>
        <input
          type="url"
          aria-label={`Your ${platform.name} profile link`}
          placeholder={`Paste your ${platform.name} profile link`}
          className={`${inputShell} min-w-[200px] flex-1 !py-2`}
          value={url}
          onChange={(event) => setUrl(event.target.value)}
        />
        <Button size="sm" disabled={!url.trim()} onClick={() => onVerify(url.trim())}>
          Verify
        </Button>
      </div>
      {result ? <Checks checks={result.checks} /> : null}
      {result?.keptPrevious ? (
        <p className="mt-1.5 text-[12.5px] text-text-muted">
          Your previously verified link is kept: {profile?.profileUrl}
        </p>
      ) : null}
    </div>
  );
}

/** Step 1: Position. Niche line, readiness audit, and a verified partner-platform profile. */
export function PositionScreen(props: PositionScreenProps) {
  const { view } = props;
  const [niche, setNiche] = useState(view.niche ?? "");
  const nicheOk = view.nicheChecks.every((check) => check.pass);
  const verified = view.profiles.filter((profile) => profile.verified).length;

  return (
    <div>
      <ScreenHeader
        kicker="Step 1 · Position"
        title="Get findable and credible."
        sub="Two quick builds: a one-line niche that tells clients exactly what you do, and a live profile on at least one partner platform."
      />
      <Guide guideKey="gig1" done={view.done} />
      <StepChecklist gate={view.gate} done={view.done} />
      <Audit
        questions={props.auditQuestions}
        result={view.audit}
        busy={props.aiBusy}
        onSubmit={props.onSubmitAudit}
      />

      <Section
        title="1. Your niche in one line"
        hint='Who you help, what you do for them, and the result. Example: "I edit short Reels for Lagos restaurants that want more walk-in customers."'
        status={<Pill ok={nicheOk} />}
      >
        <textarea
          aria-label="Your niche in one line"
          rows={2}
          placeholder="I ... for ... so that ..."
          className={inputShell}
          value={niche}
          onChange={(event) => {
            setNiche(event.target.value);
            props.onNicheChange(event.target.value);
          }}
        />
        {niche.trim() ? <Checks checks={view.nicheChecks} /> : null}
        {nicheOk ? (
          <div className="mt-3">
            <Button variant="ghost" size="sm" disabled={props.aiBusy} onClick={props.onReviewNiche}>
              {props.aiBusy ? "Thinking..." : "Get coaching on my niche line"}
            </Button>
          </div>
        ) : null}
        {view.nicheFeedback ? (
          <AIFeedbackCard feedback={view.nicheFeedback} title="Coach's note" />
        ) : null}
      </Section>

      <Banner tone="warn">
        <b>Complete KYC as you sign up.</b> Every platform asks you to verify your identity (usually
        a government ID and a selfie) before you can be paid. Do it now on each platform you join.
        In Step 4 you'll upload a screenshot showing your KYC status, and Step 4 can't be completed
        without it.
      </Banner>

      <Section
        title="2. Get live on a partner platform"
        hint="Register through the links below, complete your profile and KYC, then paste your profile link so we can confirm it's live."
      >
        <div className="flex flex-col gap-2.5">
          {props.platforms.map((platform) => (
            <PlatformCard
              key={platform.id}
              platform={platform}
              profile={view.profiles.find((p) => p.platform.id === platform.id)}
              result={props.verifyResults[platform.id]}
              onVisit={() => props.onVisit(platform.id)}
              onVerify={(url) => props.onVerify(platform.id, url)}
            />
          ))}
        </div>
      </Section>

      {verified > 0 ? (
        <Banner tone="good">
          ✓ {verified} platform{verified > 1 ? "s" : ""} verified.
          {nicheOk
            ? " You can complete Step 1 now, or add more platforms first."
            : " Now finish your niche line."}
        </Banner>
      ) : null}

      <GateFooter
        gate={view.gate}
        done={view.done}
        label="Complete Step 1"
        next={{ to: "/gig/2", label: "Continue to Step 2" }}
        busy={props.busy}
        onComplete={props.onComplete}
      />
    </div>
  );
}
